import { useEffect, useMemo, useRef, useState } from "react";
import { VestigingFilter } from "../components/VestigingFilter";
import { huidigeBtwUitHistorie, useBtwCodes } from "../components/BtwKeuze";
import { TablePager } from "../components/TablePager";
import {
  useCreate,
  useCustom,
  useCustomMutation,
  useGetLocale,
  useTranslate,
} from "@refinedev/core";
import {
  DateField,
  PlusIcon,
  RotateRightIcon,
  BuildingIcon,
  Button,
  FieldShell,
  FormCard,
  Modal,
  PencilIcon,
  SearchIcon,
  TagIcon,
  StatusBadge,
  TrashIcon,
  ConfirmDialog,
  type Column,
  type StatusTone,
} from "@fawkes/ui";

import { formatEuro, todayIso } from "../i18n/format";
import {
  SoortTegels, WeergaveKeuze, leesWeergave, bewaarWeergave,
  type Weergave, type SoortTegel,
} from "../components/CatalogusKop";
import { Boxes, Layers, Package, Pill, Shield, Syringe, Wrench } from "lucide-react";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

/** One rich catalog row — mirrors api `ProductCatalogRowResponse` (id/naam/artikelCode/onderdeel/
 *  type/scope/geldendePrijs/omzet12m/voorraadHuidig/stockMasked). */
interface ProductRow {
  id: number;
  naam: string;
  artikelCode: string | null;
  onderdeel: string | null;
  type: string | null;
  scope: string | null;
  geldendePrijs: number | null;
  omzet12m: number | null;
  voorraadHuidig: number | null;
  stockMasked: boolean;
}

/** The paged envelope — mirrors api `ProductCatalogPageResponse`. */
interface ProductPage {
  content: ProductRow[];
  totalElements: number;
  page: number;
  size: number;
}


/** The edit-form reference feeds; only `productGroepen` is used by the "+ Nieuw" dialog. */
interface Optie {
  id: number;
  label: string;
}
interface OptiesData {
  productGroepen: Optie[];
}

const PRODUCT_TYPES = ["VERKOOP", "DIENST", "ENTING"] as const;
const SCOPE_CREATE_TYPES = ["ORGANISATIE", "VESTIGING"] as const;

/** The Type filter options (Jmix: Alle / Verkoop / Dienst / Enting / Ontwormen / Medicijn). "" = Alle. */
// De soorten eerst, het totaal achteraan — zoals de tegelrij op het dierenoverzicht en op de
// tariefschermen. Het totaal is geen soort en hoort niet vooraan tussen de soorten.
const TYPE_FILTER_OPTIONS = ["VERKOOP", "DIENST", "ENTING", "ONTWORMEN", "MEDICIJN", ""] as const;

/** Het icoon per soort op de tegelrij; "" is de tegel "Alle". */
const SOORT_ICOON: Record<string, typeof Boxes> = {
  "": Layers,
  VERKOOP: Package,
  DIENST: Wrench,
  ENTING: Syringe,
  ONTWORMEN: Shield,
  MEDICIJN: Pill,
};

/** Onder welke sleutel dit scherm zijn weergavekeuze onthoudt. */
const WEERGAVE_SLEUTEL = "fawkes.producten";

/** Server page size — the api caps `size` at 50 (the PO wants the cap kept). We page, never over-fetch. */
const PAGE_SIZE = 50;

/** The catalog columns the api can sort on (ProductQueryService.SORT_FIELDS). */
type SortField = "naam" | "artikelCode";
type SortDir = "asc" | "desc";

/** Type → badge tone. Jmix renders Enting in green; Verkoop/Dienst as muted pills. */
function typeTone(type: string | null): StatusTone {
  if (type === "ENTING") return "success";
  if (type === "DIENST") return "warning";
  return "neutral";
}

/** The running-reservation strategy options (Jmix `LopendeReserveringStrategy`). Carried as UI only —
 *  the Epic-1 backend treats `strategy` as a no-op (affected count is always 0), so this radio + the
 *  "mailen" checkbox are rendered for parity but do not change server behaviour. */
const RESERVERING_STRATEGIES = [
  "BEHOUDEN_OUDE_PRIJS",
  "ALLEMAAL_AANPASSEN",
  "HANDMATIGE_SELECTIE",
] as const;
type ReserveringStrategy = (typeof RESERVERING_STRATEGIES)[number];

/** The POST /prijswijziging response — the new version + the affected-reservation count. */
interface PrijswijzigingResult {
  versieId: number;
  bedrag: number;
  effectdatum: string;
  afgeslotenEinddatum: string | null;
  getroffenReserveringen: number;
}


// products-migration (catalogus list): the catalog overview, rebuilt to Jmix `producten-catalogus`
// parity. The backend now serves a RICH, server-side filtered/sorted/PAGED list
// (`GET /api/producten?q&onderdeel&type&scope&sort=field,dir&page&size` → {content,totalElements}).
// We read that envelope via `useCustom` — NOT useList — because the shared simple-rest dataProvider's
// getList cannot read a {content,totalElements} envelope; useCustom returns the raw body so we read
// `data.content` / `data.totalElements` directly. The url is RELATIVE ("producten?…") — the
// dataProvider prepends `/api` once; a leading slash would double to /api/api.
//
// Toolbar (left→right): debounced Search (q) · de soort-tegels boven de lijst (sturen
// productgroep id) · Type filter (Alle/Verkoop/Dienst/Enting) · Vernieuwen (refetch) · "+ Nieuw"
// (the create dialog). A Scope selector (Organisatie/Vestiging/Beide, default Beide) sits top-right.
// Columns: Naam · Onderdeel · Type (colored badge) · Prijs (geldendePrijs EUR) · Scope (badge) ·
// Omzet (omzet12m EUR) · row-actions kebab. Naam/Artikelcode column headers are click-to-sort.
//
// FILTER/SORT/PAGE → QUERY PARAMS: q←debounced search; onderdeel←selected productgroep id;
// type←Type filter (omitted for Alle); scope←Scope selector (default BEIDE); sort←`${field},${dir}`;
// page←(uiPage-1) (UI is 1-based, the api page is 0-based); size←50 (the kept cap). Any filter/sort
// change resets to page 1 so the user never lands out of range.
//
// Row actions: the kebab's "Bewerken" opens the detail (onOpen). "Prijs wijzigen" and "Historie tonen"
// drive the product price seam (Epic 1, Story 1.6) — now wired to the migrated tarief endpoints:
//   POST producten/{id}/prijswijziging  (ROLE_TARIEF_EDIT; a back-dated effectdatum needs ROLE_TARIEF_ADMIN)
//   GET  producten/{id}/tarief-historie (any authenticated user)
// Both open modals (the Jmix PrijsWijzigingDialog / TariefHistorieDialog). Urls are RELATIVE — the
// dataProvider prepends /api once. The strategy radio + "klanten mailen" checkbox are carried as UI for
// parity but the Epic-1 backend treats them as a no-op; the affected-reservation count comes back on the
// POST response (always 0 in Epic 1 — there is no separate pre-submit preview endpoint) and is surfaced
// in the success notice. A back-dated 403 surfaces the "alleen beheerder mag terugwerkend" message.
//
// `voorraadHuidig` stays the field-masked attribute (FR12 axis-b): the api returns the real value to a
// stock-authorised caller, else null + stockMasked=true. There is no dedicated stock column in the
// Jmix grid, so masked stock is surfaced as an inline badge appended to the Onderdeel cell only when
// a value/flag is present — keeping the original masking tracer observable without adding a 7th column.
export function ProductList({ onOpen, vestigingIds, vestigingKeuze }: {
  onOpen?: (id: string) => void;
  /** De aangevinkte vestigingen; leeg = alle. */
  vestigingIds: number[];
  /**
   * De keuze zelf, om hem in de filterbalk te kunnen tonen — daar hoort hij, bij de filters.
   * Optioneel: wie de lijst gebruikt zonder kiezer krijgt gewoon geen kiezer te zien.
   */
  vestigingKeuze?: { opties: string[]; gekozen: string[]; onChange: (volgende: string[]) => void };
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  // Toolbar / filter state.
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [sortField, setSortField] = useState<SortField>("naam");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(1);
  // Hoeveel rijen per pagina — op acc kies je dat zelf. Tien is de oude vaste waarde en blijft de
  // standaard; wie een catalogus doorloopt zet hem op honderd en scrollt.
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  // Debounce the search box; a new search resets to page 1.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebounced(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [search]);

  // Any non-search filter/sort change also resets to page 1.
  useEffect(() => {
    setPage(1);
  }, [typeFilter, vestigingIds, sortField, sortDir]);

  const [weergave, setWeergave] = useState<Weergave>(() => leesWeergave(WEERGAVE_SLEUTEL));
  const kiesWeergave = (w: Weergave) => {
    setWeergave(w);
    bewaarWeergave(WEERGAVE_SLEUTEL, w);
  };

  const url = useMemo(() => {
    const params = new URLSearchParams();
    if (debounced) params.set("q", debounced);
    if (typeFilter) params.set("type", typeFilter);
    vestigingIds.forEach((id) => params.append("vestigingId", String(id)));
    params.set("sort", `${sortField},${sortDir}`);
    params.set("page", String(page - 1)); // UI is 1-based; the api page is 0-based.
    params.set("size", String(pageSize));
    return `producten?${params.toString()}`;
  }, [debounced, typeFilter, vestigingIds, sortField, sortDir, page, pageSize]);

  const { data, isLoading, isError, refetch } = useCustom<ProductPage>({ url, method: "get" });
  // De aantallen op de tegels: dezelfde filters als de lijst, behalve de soort zelf — anders zou
  // elke tegel na één klik "0" zeggen voor alle andere soorten.
  const aantallenUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (debounced) params.set("q", debounced);
    vestigingIds.forEach((id) => params.append("vestigingId", String(id)));
    return `producten/aantallen?${params.toString()}`;
  }, [debounced, vestigingIds]);
  const { data: aantallenData } = useCustom<Record<string, number>>({ url: aantallenUrl, method: "get" });
  const aantallen = aantallenData?.data ?? {};
  const { data: optiesData } = useCustom<OptiesData>({ url: "producten/opties", method: "get" });
  const { mutate: create, isLoading: creating } = useCreate();

  const rows = data?.data?.content ?? [];
  const total = data?.data?.totalElements ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const groepen = optiesData?.data?.productGroepen ?? [];

  // Row-action kebab: which row's menu is open (by id), and outside-click close.
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (openMenuId == null) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpenMenuId(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [openMenuId]);

  // Create dialog state — the minimum Jmix "+ Nieuw" fields (capture 19).
  const [createOpen, setCreateOpen] = useState(false);
  const [naam, setNaam] = useState("");
  const [productGroepId, setProductGroepId] = useState("");
  const [productType, setProductType] = useState<string>("VERKOOP");
  const [scopeType, setScopeType] = useState<string>("ORGANISATIE");
  const [createError, setCreateError] = useState(false);
  const [naamMissing, setNaamMissing] = useState(false);

  const openCreate = () => {
    setNaam("");
    setProductGroepId("");
    setProductType("VERKOOP");
    setScopeType("ORGANISATIE");
    setCreateError(false);
    setNaamMissing(false);
    setCreateOpen(true);
  };

  const submitCreate = () => {
    if (!naam.trim()) {
      setNaamMissing(true);
      return;
    }
    create(
      {
        resource: "producten",
        values: {
          naam: naam.trim(),
          productGroepId: productGroepId === "" ? null : Number(productGroepId),
          type: productType,
          scope: scopeType,
        },
      },
      {
        onSuccess: (res) => {
          const newId = (res.data as unknown as { id: number }).id;
          setCreateOpen(false);
          if (newId != null) onOpen?.(String(newId));
          else refetch();
        },
        onError: () => setCreateError(true),
      },
    );
  };

  // ---- Prijs wijzigen dialog (Story 1.6) ----
  // Mirrors the Jmix PrijsWijzigingDialog: current price (read-only), new bedrag (required), effectdatum
  // (required, default today), reden, the running-reservation strategy radio + "mailen" checkbox (UI-only,
  // no-op backend), and a success/error notice. The affected count comes back on the POST response.
  const { mutate: mutatePrijs, isLoading: prijsBusy } = useCustomMutation();
  // Verwijderen vraagt altijd om bevestiging: het is onomkeerbaar, en een product dat nog aan een
  // factuur of voorraad hangt kan de server weigeren (409) — die uitleg hoort in beeld te komen.
  const [teVerwijderen, setTeVerwijderen] = useState<ProductRow | null>(null);
  const [verwijderFout, setVerwijderFout] = useState<string | null>(null);
  const { mutate: verwijderProduct, isLoading: verwijderBusy } = useCustomMutation();

  const bevestigVerwijderen = () => {
    if (!teVerwijderen) return;
    verwijderProduct(
      { url: `producten/${teVerwijderen.id}`, method: "delete", values: {} },
      {
        onSuccess: () => {
          setTeVerwijderen(null);
          refetch();
        },
        onError: (err) => {
          const body = (err as { response?: { data?: { message?: string } } }).response?.data;
          setVerwijderFout(body?.message ?? t("productListView.delete.failed"));
        },
      },
    );
  };
  const [prijsOpen, setPrijsOpen] = useState(false);
  const [prijsRow, setPrijsRow] = useState<ProductRow | null>(null);
  const [prijsBedrag, setPrijsBedrag] = useState("");
  const [prijsEffectdatum, setPrijsEffectdatum] = useState("");
  const [prijsReden, setPrijsReden] = useState("");
  const [prijsStrategy, setPrijsStrategy] = useState<ReserveringStrategy>("BEHOUDEN_OUDE_PRIJS");
  const [prijsMailen, setPrijsMailen] = useState(true);
  const [prijsBedragMissing, setPrijsBedragMissing] = useState(false);
  // null = no notice; otherwise an inline notice (the affected count rides the success variant).
  const [prijsNotice, setPrijsNotice] = useState<
    | { kind: "successAffected"; aantal: number }
    | { kind: "error" | "pastForbidden" }
    | null
  >(null);
  // BTW is verplicht op elke prijsversie (0% mag, maar moet gekozen zijn). Voorgevuld met de BTW van de
  // versie die nu loopt — meestal verandert alleen het bedrag — maar altijd zichtbaar en aanpasbaar, zodat
  // een tariefwijziging van de BTW zelf (21% → 9%) hier ook kan. Pas laden als de dialoog open is.
  const prijsBtwCodes = useBtwCodes(prijsOpen);
  const { data: prijsHistData } = useCustom<{ btwCodeId: number | null; status?: string }[]>({
    url: `producten/${prijsRow?.id ?? 0}/tarief-historie`,
    method: "get",
    queryOptions: { enabled: prijsOpen && prijsRow != null, retry: false },
  });
  const huidigePrijsBtw = huidigeBtwUitHistorie(Array.isArray(prijsHistData?.data) ? prijsHistData!.data : []);
  const [prijsBtwId, setPrijsBtwId] = useState("");
  const [prijsBtwMissing, setPrijsBtwMissing] = useState(false);
  useEffect(() => {
    if (prijsOpen && huidigePrijsBtw !== "") setPrijsBtwId((b) => (b === "" ? huidigePrijsBtw : b));
  }, [prijsOpen, huidigePrijsBtw]);

  const openPrijs = (row: ProductRow) => {
    setOpenMenuId(null);
    setPrijsRow(row);
    setPrijsBedrag("");
    setPrijsEffectdatum(todayIso());
    setPrijsReden("");
    setPrijsStrategy("BEHOUDEN_OUDE_PRIJS");
    setPrijsMailen(true);
    setPrijsBedragMissing(false);
    setPrijsBtwId("");
    setPrijsBtwMissing(false);
    setPrijsNotice(null);
    setPrijsOpen(true);
  };

  const submitPrijs = () => {
    if (!prijsRow) return;
    const bedrag = Number(prijsBedrag.replace(",", "."));
    const bedragOngeldig = prijsBedrag.trim() === "" || !Number.isFinite(bedrag);
    setPrijsBedragMissing(bedragOngeldig);
    setPrijsBtwMissing(prijsBtwId === "");
    if (bedragOngeldig || prijsBtwId === "") return;
    setPrijsNotice(null);
    mutatePrijs(
      {
        url: `producten/${prijsRow.id}/prijswijziging`,
        method: "post",
        values: {
          bedrag,
          effectdatum: prijsEffectdatum || null,
          reden: prijsReden.trim() === "" ? null : prijsReden.trim(),
          btwCodeId: Number(prijsBtwId),
          // Carried for parity; the Epic-1 backend treats `strategy` as a no-op. "mailen" has no backend
          // field yet (the reservation coupling lands later), so it is intentionally not sent.
          strategy: prijsStrategy,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          const result = res.data as unknown as PrijswijzigingResult;
          setPrijsOpen(false);
          // The geldendePrijs may have changed (a today/past effectdatum becomes active) — refetch the list.
          refetch();
          // Surface the affected-reservation count from the response (0 in Epic 1) in the toast-style notice.
          setListNotice({ kind: "successAffected", aantal: result?.getroffenReserveringen ?? 0 });
        },
        onError: (err) => {
          // A back-dated effectdatum by a non-admin → 403 (terugwerkende kracht). Surface the clear message
          // rather than the generic failure; do not fake success.
          const code = (err as { statusCode?: number }).statusCode;
          setPrijsNotice({ kind: code === 403 ? "pastForbidden" : "error" });
        },
      },
    );
  };

  // A transient list-level notice (success after the dialog closes), mirroring the Jmix success toast.
  const [listNotice, setListNotice] = useState<{ kind: "successAffected"; aantal: number } | null>(null);
  useEffect(() => {
    if (!listNotice) return;
    const handle = setTimeout(() => setListNotice(null), 6000);
    return () => clearTimeout(handle);
  }, [listNotice]);

  // acc sorts by clicking the column header, so Naam carries a sortKey and the table reports the
  // intent back (the server does the sorting). The separate "Sorteren op"-row this screen used to
  // carry is gone with it — acc has no such row.
  const columns: Column<ProductRow>[] = [
    { header: t("productListView.column.naam"), sortKey: "naam", cell: (p) => p.naam },
    {
      // The Jmix catalogus list has no stock column/badge (columns are Naam/Onderdeel/Type/Prijs/
      // Scope/Omzet only), so the Onderdeel cell shows only the onderdeel (no stock badge).
      header: t("productListView.column.onderdeel"),
      cell: (p) => p.onderdeel ?? t("productListView.onderdeel.fallback"),
    },
    {
      header: t("productListView.column.type"),
      cell: (p) =>
        p.type ? (
          <StatusBadge tone={typeTone(p.type)}>{t(`productDetailView.productType.${p.type}`)}</StatusBadge>
        ) : (
          "—"
        ),
    },
    {
      header: t("productListView.column.prijs"),
      cell: (p) => formatEuro(p.geldendePrijs ?? 0, locale),
    },
    {
      header: t("productListView.column.scope"),
      cell: (p) =>
        p.scope ? (
          <StatusBadge tone="neutral">
            <span className="fawkes-products-scope">
              <BuildingIcon width={12} height={12} />
              {p.scope === "ORGANISATIE"
                ? t("productListView.scope.ORGANISATIE")
                : t(`productDetailView.scopeType.${p.scope}`)}
            </span>
          </StatusBadge>
        ) : (
          "—"
        ),
    },
    {
      header: t("productListView.column.omzet"),
      cell: (p) => formatEuro(p.omzet12m ?? 0, locale),
    },
    {
      header: "",
      align: "right",
      // Bewerken en verwijderen staan als losse knoppen in de rij. Dat zijn de twee dingen die je hier
      // doet; achter een menu verstoppen kost een klik om ze te vinden. Prijs wijzigen en de historie
      // zaten ook in dat menu — die staan in het product zelf, op de Prijzen-tegel, en zijn dus via
      // het potlood bereikbaar.
      cell: (p) => (
        <div className="fawkes-products-rowactions">
          <button
            type="button"
            className="fawkes-rowactie"
            title={t("productListView.actions.edit")}
            aria-label={t("productListView.actions.edit")}
            onClick={() => onOpen?.(String(p.id))}
          >
            <PencilIcon width={16} height={16} />
          </button>
          <button
            type="button"
            className="fawkes-rowactie"
            title={t("productListView.actions.priceChange")}
            aria-label={t("productListView.actions.priceChange")}
            onClick={() => openPrijs(p)}
          >
            <TagIcon width={16} height={16} />
          </button>
          <button
            type="button"
            className="fawkes-rowactie fawkes-rowactie--verwijder"
            title={t("productListView.actions.delete")}
            aria-label={t("productListView.actions.delete")}
            onClick={() => { setTeVerwijderen(p); setVerwijderFout(null); }}
          >
            <TrashIcon width={16} height={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <FormCard>
      {/* Story 1.6: the price-change success toast (Jmix parity) — carries the affected-reservation count. */}
      {listNotice?.kind === "successAffected" && (
        <p className="product-notice product-notice--saved" role="status">
          {listNotice.aantal > 0
            ? t("productPrijsWijziging.successAffected", { args: [listNotice.aantal] })
            : t("productPrijsWijziging.success")}
        </p>
      )}

      {/* De soortkeuze staat boven de filters, als rij grote tegels: dit is waarmee je begint, niet
          iets wat je uit een uitklaplijst moet vissen. */}
      <SoortTegels
        label={t("productListView.filter.type")}
        gekozen={typeFilter}
        onKies={setTypeFilter}
        tegels={TYPE_FILTER_OPTIONS.map<SoortTegel>((tp) => {
          const Icoon = SOORT_ICOON[tp] ?? Boxes;
          return {
            key: tp,
            label: tp ? t(`productDetailView.productType.${tp}`) : t("dierAanwezig.stat.total"),
            aantal: aantallen[tp],
            icon: <Icoon size={20} />,
          };
        })}
      />

      <div className="fawkes-products-toolbar">
        {vestigingKeuze && (
          <VestigingFilter
            label={t("financieel.filter.vestiging")}
            opties={vestigingKeuze.opties}
            gekozen={vestigingKeuze.gekozen}
            onChange={vestigingKeuze.onChange}
          />
        )}
        <div className="fawkes-products-search">
          <SearchIcon className="fawkes-products-search__icon" width={16} height={16} />
          <input
            type="search"
            autoComplete="off"
            className="fawkes-products-search__input"
            placeholder={t("productListView.search.placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={t("productListView.search.placeholder")}
          />
        </div>
        <div className="fawkes-products-toolbar__spacer" />
        <WeergaveKeuze
          weergave={weergave}
          onKies={kiesWeergave}
          labels={{
            groep: t("catalogus.weergave"),
            kaart: t("catalogus.weergave.kaart"),
            compact: t("catalogus.weergave.compact"),
            lijst: t("catalogus.weergave.lijst"),
          }}
        />
        {/* Vernieuwen als tekstregel met een rond pijltje, Nieuw als knop — zoals op acc. Die twee
            horen niet even zwaar te wegen. */}
        <button
          type="button"
          className="fawkes-products-vernieuw"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RotateRightIcon width={16} height={16} />
          {t("productListView.refresh")}
        </button>
        <Button variant="primary" onClick={openCreate}>
          <PlusIcon width={16} height={16} /> {t("productEditView.new")}
        </Button>
      </div>

      {isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : isError ? (
        <p className="fawkes-error">{t("productListView.loadFailed")}</p>
      ) : (
        <>
          <div className="fawkes-products-tabelvenster">
          {weergave !== "lijst" ? (
            <div className={`fawkes-catalogus__kaarten${weergave === "compact" ? " fawkes-catalogus__kaarten--compact" : ""}`}>
              {rows.map((rij) => (
                <article className="fawkes-catalogus__kaart" key={rij.id}>
                  <div className="fawkes-catalogus__kaart-kop">
                    <span>
                      <span className="fawkes-catalogus__kaart-naam">{rij.naam}</span>
                      {rij.artikelCode && (
                        <span className="fawkes-catalogus__kaart-code"> {rij.artikelCode}</span>
                      )}
                    </span>
                    <StatusBadge tone={typeTone(rij.type)}>
                      {rij.type ? t(`productDetailView.productType.${rij.type}`) : "—"}
                    </StatusBadge>
                  </div>
                  {weergave === "kaart" && (
                    <dl className="fawkes-catalogus__kaart-feiten">
                      <div>
                        <dt>{t("productListView.column.onderdeel")}</dt>
                        <dd>{rij.onderdeel ?? "—"}</dd>
                      </div>
                      <div>
                        <dt>{t("productListView.column.scope")}</dt>
                        <dd>{rij.scope ?? "—"}</dd>
                      </div>
                    </dl>
                  )}
                  <div className="fawkes-catalogus__kaart-voet">
                    <strong>{rij.geldendePrijs != null ? formatEuro(rij.geldendePrijs, locale) : "—"}</strong>
                    <button type="button" className="fawkes-products-vernieuw" onClick={() => onOpen?.(String(rij.id))}>
                      {t("productListView.open")}
                    </button>
                  </div>
                </article>
              ))}
              {rows.length === 0 && <p className="fawkes-muted">{t("productListView.empty")}</p>}
            </div>
          ) : (
          <IndeelbareTabel tabelKey="product-list"
            columns={columns}
            rows={rows}
            getRowKey={(p) => String(p.id)}
            empty={t("productListView.empty")}
            sort={{ key: sortField, dir: sortDir }}
            onSortChange={(next) => {
              setSortField(next.key as SortField);
              setSortDir(next.dir);
            }}
            // Jmix opens the editor on row double-click; the kebab "Bewerken" still opens it too.
            onRowDoubleClick={(p) => onOpen?.(String(p.id))}
          />
          )}
          </div>
          <TablePager
            page={page}
            pageCount={pageCount}
            total={total}
            from={from}
            to={to}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={(maat) => {
              setPageSize(maat);
              // Terug naar het begin: pagina 7 van 12 bestaat niet meer als je er honderd per
              // pagina van maakt, en dan sta je naar een lege tabel te kijken.
              setPage(1);
            }}
            labels={{
              range: t("productListView.pagination.range"),
              previous: t("productListView.pagination.previous"),
              next: t("productListView.pagination.next"),
              pageSize: t("pager.pageSize"),
              page: t("pager.page"),
            }}
          />
        </>
      )}

      <Modal
        open={createOpen}
        title={t("productEditView.create.title")}
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)} disabled={creating}>
              {t("productEditView.cancel")}
            </Button>
            <Button variant="primary" onClick={submitCreate} disabled={creating}>
              {t("productEditView.create.submit")}
            </Button>
          </>
        }
      >
        <div className="fawkes-form-grid">
          <FieldShell label={t("productEditView.create.naam")} required span2>
            <input
              className="fui-field__input"
              value={naam}
              onChange={(e) => {
                setNaam(e.target.value);
                setNaamMissing(false);
                setCreateError(false);
              }}
            />
          </FieldShell>
          <FieldShell label={t("productEditView.create.onderdeel")} span2>
            <select
              className="fui-field__input fui-select"
              value={productGroepId}
              onChange={(e) => setProductGroepId(e.target.value)}
            >
              <option value="">{t("productEditView.select.none")}</option>
              {groepen.map((g) => (
                <option key={g.id} value={String(g.id)}>
                  {g.label}
                </option>
              ))}
            </select>
          </FieldShell>
          <FieldShell label={t("productEditView.create.type")}>
            <div className="product-radio-group" role="radiogroup">
              {PRODUCT_TYPES.map((opt) => (
                <label key={opt} className="product-radio">
                  <input
                    type="radio"
                    name="createProductType"
                    value={opt}
                    checked={productType === opt}
                    onChange={() => setProductType(opt)}
                  />
                  {t(`productDetailView.productType.${opt}`)}
                </label>
              ))}
            </div>
          </FieldShell>
          <FieldShell label={t("productEditView.create.scope")}>
            <div className="product-radio-group" role="radiogroup">
              {SCOPE_CREATE_TYPES.map((opt) => (
                <label key={opt} className="product-radio">
                  <input
                    type="radio"
                    name="createScopeType"
                    value={opt}
                    checked={scopeType === opt}
                    onChange={() => setScopeType(opt)}
                  />
                  {t(`productDetailView.scopeType.${opt}`)}
                </label>
              ))}
            </div>
          </FieldShell>
        </div>
        {naamMissing && (
          <p className="product-notice product-notice--required" role="status">
            {t("productEditView.naamRequired")}
          </p>
        )}
        {createError && (
          <p className="product-notice product-notice--error" role="status">
            {t("productEditView.create.error")}
          </p>
        )}
      </Modal>

      {teVerwijderen && (
        <ConfirmDialog
          title={t("productListView.delete.title")}
          confirmLabel={t("productListView.delete.confirm")}
          cancelLabel={t("common.cancel")}
          danger
          busy={verwijderBusy}
          onConfirm={bevestigVerwijderen}
          onCancel={() => setTeVerwijderen(null)}
        >
          <p>{t("productListView.delete.body", { args: [teVerwijderen.naam] })}</p>
          {verwijderFout && (
            <p className="fawkes-error" role="alert">{verwijderFout}</p>
          )}
        </ConfirmDialog>
      )}

      {/* Prijs wijzigen (Story 1.6) — the Jmix PrijsWijzigingDialog ported to a Modal. */}
      <Modal
        open={prijsOpen}
        title={t("productPrijsWijziging.title", { args: [prijsRow?.naam ?? ""] })}
        onClose={() => setPrijsOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPrijsOpen(false)} disabled={prijsBusy}>
              {t("productPrijsWijziging.cancel")}
            </Button>
            <Button variant="primary" onClick={submitPrijs} disabled={prijsBusy}>
              {t("productPrijsWijziging.submit")}
            </Button>
          </>
        }
      >
        <div className="fawkes-form-grid">
          <FieldShell label={t("productPrijsWijziging.current")}>
            <input
              className="fui-field__input"
              value={formatEuro(prijsRow?.geldendePrijs ?? 0, locale)}
              readOnly
            />
          </FieldShell>
          <FieldShell label={t("productPrijsWijziging.bedrag")} required>
            <input
              className="fui-field__input"
              inputMode="decimal"
              aria-label={t("productPrijsWijziging.bedrag")}
              value={prijsBedrag}
              onChange={(e) => {
                setPrijsBedrag(e.target.value);
                setPrijsBedragMissing(false);
                setPrijsNotice(null);
              }}
            />
          </FieldShell>
          <FieldShell label={t("productPrijsWijziging.effectdatum")} required>
            <DateField className="fui-field__input"
              aria-label={t("productPrijsWijziging.effectdatum")}
              value={prijsEffectdatum}
              onChange={(e) => {
                setPrijsEffectdatum(e.target.value);
                setPrijsNotice(null);
              }}
            />
          </FieldShell>
          <FieldShell label={t("btw.label")} required>
            <select
              className="fui-field__input fui-select"
              aria-label={t("btw.label")}
              required
              value={prijsBtwId}
              onChange={(e) => {
                setPrijsBtwId(e.target.value);
                setPrijsBtwMissing(false);
                setPrijsNotice(null);
              }}
            >
              <option value="">{t("btw.kies")}</option>
              {prijsBtwCodes.map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {b.label}
                </option>
              ))}
            </select>
          </FieldShell>
          <FieldShell label={t("productPrijsWijziging.reden")} span2>
            <input
              className="fui-field__input"
              maxLength={500}
              value={prijsReden}
              onChange={(e) => setPrijsReden(e.target.value)}
            />
          </FieldShell>
        </div>

        {/* The running-reservation strategy + mail checkbox are carried as UI for Jmix parity; the
            Epic-1 backend acts on neither (the reservation coupling lands in a later phase). */}
        <FieldShell label={t("productPrijsWijziging.strategy.label")} span2>
          <div className="product-radio-group" role="radiogroup">
            {RESERVERING_STRATEGIES.map((opt) => (
              <label key={opt} className="product-radio">
                <input
                  type="radio"
                  name="prijsStrategy"
                  value={opt}
                  checked={prijsStrategy === opt}
                  onChange={() => setPrijsStrategy(opt)}
                />
                {t(`productPrijsWijziging.strategy.${opt}`)}
              </label>
            ))}
          </div>
        </FieldShell>
        <label className="product-radio">
          <input
            type="checkbox"
            checked={prijsMailen}
            onChange={(e) => setPrijsMailen(e.target.checked)}
          />
          {t("productPrijsWijziging.mailen")}
        </label>

        {prijsBedragMissing && (
          <p className="product-notice product-notice--required" role="status">
            {t("productPrijsWijziging.bedragRequired")}
          </p>
        )}
        {prijsBtwMissing && (
          <p className="product-notice product-notice--required" role="status">
            {t("btw.verplicht")}
          </p>
        )}
        {prijsNotice?.kind === "pastForbidden" && (
          <p className="product-notice product-notice--error" role="status">
            {t("productPrijsWijziging.pastForbidden")}
          </p>
        )}
        {prijsNotice?.kind === "error" && (
          <p className="product-notice product-notice--error" role="status">
            {t("productPrijsWijziging.error")}
          </p>
        )}
      </Modal>

    </FormCard>
  );
}
